/**
 * Right-hand rail beside the filter/sparkline/table lockup: "Live" (the
 * active run switcher + card) stacked above "Upcoming" (due-soon schedules).
 * Renders nothing — not an empty column — when there's neither a live run nor
 * anything upcoming, so the lockup below/beside it takes the full width.
 *
 * @module ui/pages/runs/test-runs/RunsRail
 */
import type { StatusIconBarItem } from '@chatsdet/ui';
import type { LiveRun } from '../../../../features/workflows/useLiveWorkflowTestExecutions';
import type { RunSummaryData } from '../../../../features/workflows/useRunSummaryData';
import type { ConnectionState } from '../../../../hooks/useWorkflowTestStream';
import { UpcomingScheduledRuns, useUpcomingScheduledRuns } from '../UpcomingScheduledRuns';
import { LiveRunsSection } from './LiveRunsSection';

export type RunsRailProps = {
  liveRuns: LiveRun[];
  /**
   * The run `LiveRunsSection` shows — resolved once by `TestRunsPage`
   * (`runs.find(r => r.suiteId === selectedRunId) ?? runs[0]`), the same
   * value used to build `liveData`/`connectionState`/`onRetry` below. Passed
   * straight through rather than re-derived from `liveRuns`/`selectedRunId`
   * here, so there's exactly one place that rule lives.
   */
  selectedRun: LiveRun | null;
  onRunSelect: (id: string) => void;
  liveData: RunSummaryData | null;
  connectionState?: ConnectionState;
  onRetry?: () => void;
  onItemSelect?: (item: StatusIconBarItem) => void;
  onUpcomingRunTriggered: () => void;
};

export function RunsRail({
  liveRuns,
  selectedRun,
  onRunSelect,
  liveData,
  connectionState,
  onRetry,
  onItemSelect,
  onUpcomingRunTriggered,
}: RunsRailProps) {
  const upcoming = useUpcomingScheduledRuns();

  const hasLive = Boolean(selectedRun) && Boolean(liveData);
  const hasUpcoming = upcoming.length > 0;
  if (!hasLive && !hasUpcoming) return null;

  return (
    <div className="flex flex-col gap-4 xl:w-[320px] xl:shrink-0">
      {selectedRun && liveData && (
        <LiveRunsSection
          runs={liveRuns}
          selectedRun={selectedRun}
          onRunSelect={onRunSelect}
          data={liveData}
          connectionState={connectionState}
          onRetry={onRetry}
          onItemSelect={onItemSelect}
        />
      )}
      {hasUpcoming && (
        <UpcomingScheduledRuns upcoming={upcoming} onRunTriggered={onUpcomingRunTriggered} />
      )}
    </div>
  );
}
